import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { signUp as authSignUp, signIn as authSignIn, logOut as authLogOut, getUserProfile, UserProfile } from '../services/authService';

interface AuthContextType {
    currentUser: User | null;
    userProfile: UserProfile | null;
    signUp: (email: string, password: string, displayName?: string) => Promise<void>;
    signIn: (email: string, password: string) => Promise<void>;
    logOut: () => Promise<void>;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
          const unsubscribe = auth.onAuthStateChanged(async (user) => {
                  setCurrentUser(user);
                  setLoading(false);
          });

          return unsubscribe;
    }, []);

    // Real-time listener for user profile (including credits)
    useEffect(() => {
          if (!currentUser) {
                  setUserProfile(null);
                  return;
          }

          const userDocRef = doc(db, 'users', currentUser.uid);
          const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
                  if (docSnap.exists()) {
                            setUserProfile(docSnap.data() as UserProfile);
                  } else {
                            setUserProfile(null);
                  }
          }, (error) => {
                  console.error('Error listening to user profile:', error);
          });

          return unsubscribe;
    }, [currentUser]);

    const signUp = async (email: string, password: string, displayName?: string) => {
          await authSignUp(email, password, displayName);
    };

    const signIn = async (email: string, password: string) => {
          await authSignIn(email, password);
    };

    const logOut = async () => {
          await authLogOut();
    };

    const value: AuthContextType = {
          currentUser,
          userProfile,
          signUp,
          signIn,
          logOut,
          loading,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
          throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
